import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import FilterDropdown, {FilterDropdownRef} from './FilterDropdown';
import FilterLayout from './FilterLayout';
import './filters.scss';

interface DateRangeFilterProps {
  yearFrom?: number;
  yearTo?: number;
  onFilterChange: (yearFrom?: number, yearTo?: number) => void;
}

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  yearFrom,
  yearTo,
  onFilterChange
}) => {
  const {t} = useTranslation();
  const [tempYearFrom, setTempYearFrom] = useState<string>(yearFrom?.toString() || '');
  const [tempYearTo, setTempYearTo] = useState<string>(yearTo?.toString() || '');
  const filterDropdownRef = useRef<FilterDropdownRef>(null);

  const hasActiveFilter = yearFrom !== undefined || yearTo !== undefined;

  // Sync temp values when props change
  useEffect(() => {
    setTempYearFrom(yearFrom?.toString() || '');
    setTempYearTo(yearTo?.toString() || '');
  }, [yearFrom, yearTo]);

  const applyFilter = () => {
    const fromYear = tempYearFrom.trim() ? parseInt(tempYearFrom.trim()) : undefined;
    const toYear = tempYearTo.trim() ? parseInt(tempYearTo.trim()) : undefined;

    // Only apply if values are valid numbers or empty
    if ((tempYearFrom.trim() === '' || !isNaN(fromYear!)) &&
      (tempYearTo.trim() === '' || !isNaN(toYear!))) {
      onFilterChange(fromYear, toYear);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      applyFilter();
      filterDropdownRef.current?.close();
    } else if (event.key === 'Escape') {
      // Reset temp values to current filter values and close dropdown
      setTempYearFrom(yearFrom?.toString() || '');
      setTempYearTo(yearTo?.toString() || '');
      filterDropdownRef.current?.close();
    }
  };

  const handleApplyClick = () => {
    applyFilter();
    filterDropdownRef.current?.close();
  };

  const clearFilter = () => {
    setTempYearFrom('');
    setTempYearTo('');
    onFilterChange(undefined, undefined);
    closeDropdown();
  };

  const closeDropdown = () => {
    filterDropdownRef.current?.close();
  }

  return (
    <FilterDropdown
      ref={filterDropdownRef}
      label={t('gallery.filters.period')}
      hasActiveFilter={hasActiveFilter}
      onClearFilter={clearFilter}
    >
      <FilterLayout
        title={t('gallery.filters.filterByPeriod')}
        onClear={clearFilter}
        onApply={handleApplyClick}
      >
        <div className="flex items-center space-x-3 px-4">
          <div className="flex-1">
            <label className="block text-xs text-gray-500 mb-1">{t('gallery.filters.fromYear')}</label>
            <input
              type="number"
              value={tempYearFrom}
              onChange={(e) => setTempYearFrom(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="e.g. 1800"
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
              min="1"
              max="2100"
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs text-gray-500 mb-1">{t('gallery.filters.toYear')}</label>
            <input
              type="number"
              value={tempYearTo}
              onChange={(e) => setTempYearTo(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="e.g. 1900"
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
              min="1"
              max="2100"
            />
          </div>
        </div>
      </FilterLayout>
    </FilterDropdown>
  );
};

export default DateRangeFilter;
